import { useEffect, useMemo, useRef, useState } from "react";
import useDebouncedValue from "../hooks/useDebouncedValue";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import PublicHeader from "../components/public/PublicHeader";
import HowItWorksSection from "../components/public/HowItWorksSection";
import CareMatchingWizard from "../components/public/CareMatchingWizard";
import PricingHeroPlan from "../components/public/PricingHeroPlan";
import SafetyTrustBanner from "../components/public/SafetyTrustBanner";
import SavingsBanner from "../components/public/SavingsBanner";
import NemaAssuranceSection from "../components/public/NemaAssuranceSection";
import TrustHighlightsBar from "../components/public/TrustHighlightsBar";
import NpidDashboardSection from "../components/public/NpidDashboardSection";
import LandingFaqSection from "../components/public/LandingFaqSection";
import CareManagerPremiumSection from "../components/public/CareManagerPremiumSection";
import LandingSiteFooter from "../components/public/LandingSiteFooter";
import PreferredCaregiversPanel, {
  MAX_CAREGIVER_PREFERENCES,
} from "../components/marketplace/PreferredCaregiversPanel";
import MarketplaceFilterPanel from "../components/marketplace/MarketplaceFilterPanel";
import { createDefaultFilterState, DEFAULT_MARKETPLACE_CARE_TYPE, DEFAULT_MARKETPLACE_RADIUS_KM, DEFAULT_SEARCH_SHIFT, DEFAULT_SEARCH_TIME, getMarketplaceTodayIsoDate, marketplaceRadiusForLocation, NEAR_ME_RADIUS_KM, planTypeFromSearchShift, searchShiftToHours, searchShiftToShiftType } from "../config/marketplaceFilterEngine";
import { CARE_TYPES, SEARCH_SHIFT_OPTIONS } from "../config/marketplaceFilterEngine";
import {
  getCareTypeAvailability,
  resolveCareTypeAvailability,
} from "../config/careServiceAvailability";
import { applyMarketplaceFilters } from "../utils/marketplaceFilterApply";
import { filtersToMarketplaceApiParams } from "../utils/marketplaceApiFilters";
import { captureNearMeMarketplaceLocation } from "../utils/marketplaceNearMe";
import { mapMarketplaceCaregiver } from "../utils/mapMarketplaceCaregiver";
import {
  canSubmitMarketplaceSearch,
  getMarketplaceSearchValidationError,
  isMarketplaceDateComplete,
  isMarketplaceLocationComplete,
} from "../utils/marketplaceSearchValidation";
import Login from "./Login";
import api from "../services/api";
import { clearAuthOverlayParams } from "../utils/authNavigation";
import { AUTH_ACTIONS, requireCustomerAuth } from "../utils/protectedAuthAction";
import { useResumeProtectedAction } from "../hooks/useResumeProtectedAction";
import { showToast } from "../utils/toast";
import FreemiumPaywall from "../components/marketplace/FreemiumPaywall";
import CareAttendantBookingDialog from "../components/marketplace/CareAttendantBookingDialog";
import MarketplaceCaregiverMap from "../components/marketplace/MarketplaceCaregiverMap";
import { useFreemiumAccess } from "../hooks/useFreemiumAccess";
import { useMarketplaceExotelCall } from "../hooks/useMarketplaceExotelCall";
import { useCallCreditGate } from "../hooks/useCallCreditGate";
import { usePlatformConfig } from "../context/PlatformConfigContext";
import {
  MarketplaceHero,
  MarketplaceSearchBar,
  CaregiverListingCard,
  MarketplaceResultsToolbar,
  formatCaregiverRating,
  formatCaregiverMoney,
} from "../components/marketplace/MarketplaceBrowseUI";
import { resolveIndianCity, DEFAULT_MARKETPLACE_LOCATION } from "../config/indianCities";
import {
  initialMarketplaceLocationState,
  persistMarketplaceLocation,
  readPersistedMarketplaceLocation,
} from "../utils/marketplaceLocationPersistence";
import {
  readStoredCaregiverPreferences,
  recordViewedCaregiver,
  writeStoredCaregiverPreferences,
} from "../utils/caregiverPreferences";
import "./LandingPage.css";
import "../components/marketplace/PreferredCaregiversPanel.css";

const EMPTY_COORDS = { latitude: "", longitude: "" };
const FALLBACK_ENABLED_CARE_TYPES = [DEFAULT_MARKETPLACE_CARE_TYPE, "nurses", "physio"];
const FALLBACK_ENABLED_SERVICE_TYPES = SEARCH_SHIFT_OPTIONS.map((option) => option.value);

function getInitials(name) {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("") || "CG";
}

function formatRating(value, reviewCount) {
  return formatCaregiverRating(value, reviewCount);
}

function formatMoney(value, suffix = "") {
  return formatCaregiverMoney(value, suffix) || "—";
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const showAuthOverlay = searchParams.get("auth") === "login";
  const isSignedIn = Boolean(api.getToken?.());
  const showShortlist = !isSignedIn;
  const initialLocation = useMemo(() => initialMarketplaceLocationState(), []);
  const [coords, setCoords] = useState(() => initialLocation.coords);
  const [locationLabel, setLocationLabel] = useState(() => initialLocation.locationLabel);
  const [serviceTab, setServiceTab] = useState(DEFAULT_MARKETPLACE_CARE_TYPE);
  const [careTypeEngaged, setCareTypeEngaged] = useState(true);
  const [searchTime, setSearchTime] = useState(DEFAULT_SEARCH_TIME);
  const [searchShift, setSearchShift] = useState(DEFAULT_SEARCH_SHIFT);
  /** True once the customer explicitly picks a shift — the default only filters the list. */
  const [searchShiftChosen, setSearchShiftChosen] = useState(false);
  const [searchDate, setSearchDate] = useState(() => getMarketplaceTodayIsoDate());
  const [radiusKm, setRadiusKm] = useState(DEFAULT_MARKETPLACE_RADIUS_KM);
  const [nearMeActive, setNearMeActive] = useState(false);
  const [nearMeLoading, setNearMeLoading] = useState(false);
  const [filters, setFilters] = useState(() => createDefaultFilterState(DEFAULT_MARKETPLACE_CARE_TYPE));
  const [searchError, setSearchError] = useState("");
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const marketplaceLoadGenRef = useRef(0);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [selectedMapCaregiverId, setSelectedMapCaregiverId] = useState(null);
  const [sortBy, setSortBy] = useState("recommended");
  const [preferences, setPreferences] = useState(() => readStoredCaregiverPreferences());
  const [dateBlockedPreferenceIds, setDateBlockedPreferenceIds] = useState(() => new Set());
  const [hireCaregiver, setHireCaregiver] = useState(null);
  const [bookingResumeDraft, setBookingResumeDraft] = useState(null);
  const [resumeAfterPaywall, setResumeAfterPaywall] = useState(false);
  const location = useLocation();
  const { config: platformConfig, loading: platformConfigLoading } = usePlatformConfig();
  const {
    paywallOpen,
    closePaywall,
    refresh: refreshFreemium,
    handleFreemiumError,
  } = useFreemiumAccess({ enabled: Boolean(api.getToken?.()) });
  /** User picked a city / Near me — don't let auto-locate clobber their choice. */
  const userOverrideLocationRef = useRef(false);

  useEffect(() => {
    writeStoredCaregiverPreferences(preferences);
  }, [preferences]);

  // Bind the customer's real location by default instead of leaving the generic
  // city fallback (initialMarketplaceLocationState()) in place until they notice
  // and tap "Near me" themselves — that generic city centroid is what collapses
  // caregiver distances to "0 km away". Runs once, silently: no toast on denial/
  // failure, since the default city fallback already covers that case, and no
  // care-type/sort/radius side effects (unlike the manual "Near me" button).
  useEffect(() => {
    if (userOverrideLocationRef.current) return;
    const persisted = readPersistedMarketplaceLocation();
    // A persisted entry only counts as a real commitment if it isn't just the
    // generic default re-saved verbatim (e.g. by an older build, or a stale
    // sessionStorage entry from before this fix) — otherwise it would keep
    // blocking auto-locate on every reload in the same tab, forever.
    const isGenericDefault =
      persisted &&
      Math.abs(Number(persisted.latitude) - DEFAULT_MARKETPLACE_LOCATION.latitude) < 1e-4 &&
      Math.abs(Number(persisted.longitude) - DEFAULT_MARKETPLACE_LOCATION.longitude) < 1e-4;
    if (persisted && !isGenericDefault) return;
    let cancelled = false;
    captureNearMeMarketplaceLocation()
      .then((result) => {
        if (cancelled || !result?.ok || result.usedFallback || userOverrideLocationRef.current) return;
        setCoords(result.coords);
        setLocationLabel(result.locationLabel);
        persistMarketplaceLocation({
          label: result.locationLabel,
          latitude: result.coords?.latitude,
          longitude: result.coords?.longitude,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only auto-locate
  }, []);

  useEffect(() => {
    if (!location.state?.bookingResume) return;
    if (!api.getToken?.()) return;
    try {
      const raw = sessionStorage.getItem("careHubBookingDraft");
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft?.caregiverId) return;
      setBookingResumeDraft(draft);
      setHireCaregiver((prev) => {
        if (prev && String(prev.id || prev.caregiverId) === String(draft.caregiverId)) return prev;
        return {
          id: draft.caregiverId,
          caregiverId: draft.caregiverId,
          name: draft.caregiverName || "Caregiver",
        };
      });
    } catch {
      /* ignore */
    }
  }, [location.state?.bookingResume]);

  const planType = useMemo(() => planTypeFromSearchShift(searchShift), [searchShift]);
  const searchHours = useMemo(() => searchShiftToHours(searchShift), [searchShift]);

  const marketplaceTypes = platformConfig?.marketplaceTypes || null;
  const enabledCareTypes = marketplaceTypes?.enabledCareTypes || FALLBACK_ENABLED_CARE_TYPES;
  const enabledServiceTypes = marketplaceTypes?.enabledServiceTypes || FALLBACK_ENABLED_SERVICE_TYPES;

  const careTypeAvailability = useMemo(
    () => resolveCareTypeAvailability(marketplaceTypes),
    [marketplaceTypes]
  );

  const careTypeTabs = useMemo(() => {
    const list = (enabledCareTypes || [])
      .map((id) => {
        const v = String(id || "").trim().toLowerCase();
        if (!v) return null;
        const cfg = CARE_TYPES[v];
        if (!cfg) return null;
        const availability = careTypeAvailability[v] || getCareTypeAvailability(v, marketplaceTypes);
        return {
          id: v,
          label: cfg.label,
          status: availability.status,
          statusLabel: availability.statusLabel,
          bookingEnabled: availability.bookingEnabled,
        };
      })
      .filter(Boolean);
    return list.length
      ? list
      : [
          {
            id: DEFAULT_MARKETPLACE_CARE_TYPE,
            label: CARE_TYPES[DEFAULT_MARKETPLACE_CARE_TYPE]?.label,
            ...getCareTypeAvailability(DEFAULT_MARKETPLACE_CARE_TYPE, marketplaceTypes),
          },
        ];
  }, [enabledCareTypes, careTypeAvailability, marketplaceTypes]);

  const activeCareTypeBookingEnabled = Boolean(
    getCareTypeAvailability(serviceTab, marketplaceTypes)?.bookingEnabled
  );

  const baseShiftOptions = useMemo(() => {
    const enabled = new Set((enabledServiceTypes || []).map((v) => String(v || "").trim().toLowerCase()));
    return SEARCH_SHIFT_OPTIONS.filter((o) => enabled.has(String(o.value)));
  }, [enabledServiceTypes]);

  const shiftOptionsForCareType = useMemo(() => {
    if (serviceTab === "physio") return baseShiftOptions.filter((o) => o.value === "hourly");
    return baseShiftOptions;
  }, [serviceTab, baseShiftOptions]);

  const shiftAllowedForCurrentCareType = useMemo(() => {
    return shiftOptionsForCareType.some((o) => o.value === searchShift);
  }, [shiftOptionsForCareType, searchShift]);

  // Keep care type and shift selection valid after admin config changes.
  useEffect(() => {
    if (platformConfigLoading) return;
    if (!serviceTab) return;
    const enabled = new Set((enabledCareTypes || []).map((v) => String(v).toLowerCase()));
    if (!enabled.has(String(serviceTab).toLowerCase())) {
      const next = (enabledCareTypes || [DEFAULT_MARKETPLACE_CARE_TYPE])[0] || DEFAULT_MARKETPLACE_CARE_TYPE;
      setServiceTab(next);
    }
  }, [platformConfigLoading, enabledCareTypes, serviceTab]);

  useEffect(() => {
    if (platformConfigLoading) return;
    if (serviceTab === "physio") {
      // Requirement: physiotherapist shows only hourly service type.
      setSearchShift("hourly");
      return;
    }
    if (shiftOptionsForCareType.length && !shiftAllowedForCurrentCareType) {
      setSearchShift(shiftOptionsForCareType[0].value);
    }
  }, [platformConfigLoading, serviceTab, shiftOptionsForCareType, shiftAllowedForCurrentCareType]);

  // Bind home search shift selection to filter shiftType (e.g. 24h → 24hour live-in).
  useEffect(() => {
    if (platformConfigLoading) return;
    const nextShiftType = searchShiftToShiftType(searchShift) || "";
    setFilters((prev) => {
      if ((prev?.shiftType || "") === nextShiftType) return prev;
      return { ...prev, shiftType: nextShiftType };
    });
  }, [platformConfigLoading, searchShift]);

  const preferenceIds = useMemo(
    () => new Set(preferences.map((p) => String(p.id))),
    [preferences]
  );

  const filteredPreferences = useMemo(() => {
    const enabled = new Set((enabledCareTypes || []).map((v) => String(v || "").toLowerCase()));
    return preferences.filter((p) => {
      if (dateBlockedPreferenceIds.has(String(p?.id))) return false;
      const ct = String(p?.careType || p?.serviceRole || "").toLowerCase();
      if (!ct) return false;
      return enabled.has(ct);
    });
  }, [preferences, enabledCareTypes, dateBlockedPreferenceIds]);

  useEffect(() => {
    let cancelled = false;
    async function loadBlockedPrefs() {
      const date = String(searchDate || "").slice(0, 10);
      const ids = preferences.map((p) => p?.id).filter(Boolean);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !ids.length) {
        if (!cancelled) setDateBlockedPreferenceIds(new Set());
        return;
      }
      try {
        const res = await api.getCaregiversUnavailableOnDate({
          serviceDate: date,
          caregiverIds: ids,
        });
        const blocked = Array.isArray(res?.unavailableCaregiverIds)
          ? res.unavailableCaregiverIds
          : Array.isArray(res?.data?.unavailableCaregiverIds)
            ? res.data.unavailableCaregiverIds
            : [];
        if (!cancelled) {
          setDateBlockedPreferenceIds(new Set(blocked.map((id) => String(id))));
        }
      } catch {
        if (!cancelled) setDateBlockedPreferenceIds(new Set());
      }
    }
    loadBlockedPrefs();
    return () => {
      cancelled = true;
    };
  }, [searchDate, preferences]);

  function closeAuthOverlay() {
    setSearchParams(clearAuthOverlayParams(searchParams), { replace: true });
  }

  function authSearchContext() {
    return {
      searchLocation: locationLabel || undefined,
      lat: coords.latitude || undefined,
      lng: coords.longitude || undefined,
      date: searchDate || undefined,
      planType: planType || undefined,
      searchTime,
      searchHours,
    };
  }

  const { runGatedCall, openExhausted, gateModal } = useCallCreditGate({ refreshFreemium });

  const { startCall, inAppCallSheet } = useMarketplaceExotelCall({
    navigate,
    handleFreemiumError,
    refreshFreemium,
    runGatedCall,
    openExhausted,
    authOptionsFor: (caregiver) => ({
      basePath: "/",
      returnTo: "/",
      caregiverId: caregiver?.id || caregiver?.caregiverId,
      action: AUTH_ACTIONS.CALL,
      tab: "create",
      ...authSearchContext(),
    }),
  });

  function performCall(caregiver) {
    startCall(caregiver);
  }

  function handleCall(caregiver) {
    performCall(caregiver);
  }

  useEffect(() => {
    const hash = window.location.hash?.replace("#", "");
    if (!hash) return;
    requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  useEffect(() => {
    if (!serviceTab) return;
    setFilters((prev) => ({ ...createDefaultFilterState(serviceTab), ...prev, careType: serviceTab }));
  }, [serviceTab]);

  const locationComplete = isMarketplaceLocationComplete(locationLabel, coords);
  const dateComplete = isMarketplaceDateComplete(searchDate);
  const canSearch =
    activeCareTypeBookingEnabled &&
    canSubmitMarketplaceSearch({
      careTypeEngaged,
      serviceTab,
      locationLabel,
      coords,
      searchDate,
    }) &&
    shiftOptionsForCareType.length > 0 &&
    shiftAllowedForCurrentCareType;
  const promptLocation = careTypeEngaged && !locationComplete;
  const promptDate = careTypeEngaged && !dateComplete;

  const debouncedSalaryHourlyMin = useDebouncedValue(filters.salaryHourlyMin, 400);
  const debouncedSalaryHourlyMax = useDebouncedValue(filters.salaryHourlyMax, 400);
  const debouncedSalaryMonthlyMin = useDebouncedValue(filters.salaryMonthlyMin, 400);
  const debouncedSalaryMonthlyMax = useDebouncedValue(filters.salaryMonthlyMax, 400);

  useEffect(() => {
    const controller = new AbortController();
    const gen = ++marketplaceLoadGenRef.current;

    async function load() {
      if (!canSearch || !coords.latitude || !coords.longitude) {
        if (marketplaceLoadGenRef.current === gen) setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const activeFilters = {
          ...filters,
          careType: serviceTab || "attendant",
          minRating: filters.minRating,
          salaryHourlyMin: debouncedSalaryHourlyMin,
          salaryHourlyMax: debouncedSalaryHourlyMax,
          salaryMonthlyMin: debouncedSalaryMonthlyMin,
          salaryMonthlyMax: debouncedSalaryMonthlyMax,
        };
        const filterParams = filtersToMarketplaceApiParams(activeFilters, { serviceTab, radiusKm });
        const res = await api.getPublicMarketplaceCaregivers({
          latitude: coords.latitude,
          longitude: coords.longitude,
          ...filterParams,
          serviceDate: searchDate,
          signal: controller.signal,
        });
        if (marketplaceLoadGenRef.current !== gen) return;
        const payload = res?.data && typeof res.data === "object" ? res.data : res;
        const list = Array.isArray(payload?.qualified) ? payload.qualified : [];
        setCaregivers(list.map(mapMarketplaceCaregiver).filter(Boolean));
      } catch (err) {
        if (marketplaceLoadGenRef.current !== gen || err?.isAbort) return;
        setError(err.message || "Unable to load caregivers.");
        setCaregivers([]);
      } finally {
        if (marketplaceLoadGenRef.current === gen) setLoading(false);
      }
    }
    load();
    return () => {
      controller.abort();
      marketplaceLoadGenRef.current += 1;
    };
    // moreFiltersOpen is UI-only — omit so expanding the panel does not re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional filter fingerprint
  }, [
    canSearch,
    coords.latitude,
    coords.longitude,
    radiusKm,
    serviceTab,
    searchDate,
    filters.patientConditions,
    filters.languages,
    filters.gender,
    filters.ageGroup,
    filters.minExperience,
    filters.verifiedOnly,
    filters.skills,
    filters.religion,
    filters.foodPreference,
    filters.sleepArrangement,
    filters.availableNow,
    filters.minRating,
    filters.shiftType,
    debouncedSalaryHourlyMin,
    debouncedSalaryHourlyMax,
    debouncedSalaryMonthlyMin,
    debouncedSalaryMonthlyMax,
    filters.salaryServiceType,
    filters.nurseClinical,
    filters.nurseQualifications,
    filters.nurseSkills,
    filters.physioRequirements,
    filters.physioFrequency,
    filters.physioDuration,
    filters.consultationType,
  ]);

  const filtered = useMemo(() => {
    let list = applyMarketplaceFilters(caregivers, { ...filters, careType: serviceTab || "attendant" });
    if (nearMeActive) {
      const nearCap = Number(radiusKm) > 0 ? Number(radiusKm) : NEAR_ME_RADIUS_KM;
      list = list.filter(
        (c) => !Number.isFinite(Number(c.distanceKm)) || Number(c.distanceKm) <= nearCap
      );
    }
    if (sortBy === "rating") {
      list = [...list].sort((a, b) => Number(b.rating || 0) - Number(a.rating || 0));
    } else if (sortBy === "distance") {
      list = [...list].sort((a, b) => Number(a.distanceKm || 99) - Number(b.distanceKm || 99));
    }
    return list;
  }, [caregivers, filters, serviceTab, sortBy, nearMeActive, radiusKm]);

  useEffect(() => {
    if (!filtered.length) {
      setSelectedMapCaregiverId(null);
      return;
    }
    setSelectedMapCaregiverId((prev) => {
      if (prev && filtered.some((c) => String(c.id) === String(prev))) return prev;
      return String(filtered[0].id);
    });
  }, [filtered]);

  const resultsTitle = canSearch
    ? filtered.length === 1
      ? "1 verified caregiver matches your search"
      : `${filtered.length.toLocaleString("en-IN")} verified caregivers match your search`
    : null;

  function clearFilters() {
    setFilters(createDefaultFilterState(serviceTab || "attendant"));
  }

  function addToPreference(c) {
    if (!c?.id || preferenceIds.has(String(c.id))) return;
    if (preferences.length >= MAX_CAREGIVER_PREFERENCES) {
      showToast.error(`You can only add up to ${MAX_CAREGIVER_PREFERENCES} caregivers.`);
      return;
    }
    if (!c.isAvailable) return;
    setPreferences((prev) => [...prev, c]);
    showToast.success(`${c.name} added to Preference ${preferences.length + 1}.`);
  }

  function removeFromPreference(id) {
    setPreferences((prev) => prev.filter((p) => String(p.id) !== String(id)));
  }

  function movePreference(fromIndex, toIndex) {
    if (toIndex < 0 || toIndex >= preferences.length || fromIndex === toIndex) return;
    setPreferences((prev) => {
      const next = [...prev];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      return next;
    });
  }

  function handleHire(caregiver) {
    if (!activeCareTypeBookingEnabled) {
      const availability = getCareTypeAvailability(serviceTab, marketplaceTypes);
      showToast.info(`${CARE_TYPES[serviceTab]?.label || "This service"} is ${availability.statusLabel}.`);
      return;
    }
    if (coords?.latitude && coords?.longitude) {
      persistMarketplaceLocation({
        label: locationLabel,
        latitude: coords.latitude,
        longitude: coords.longitude,
      });
      try {
        const raw = sessionStorage.getItem("careHubBookingDraft");
        const existing = raw ? JSON.parse(raw) : {};
        sessionStorage.setItem(
          "careHubBookingDraft",
          JSON.stringify({
            ...(existing && typeof existing === "object" ? existing : {}),
            caregiverId: caregiver?.id || caregiver?.caregiverId,
            caregiverName: caregiver?.name || caregiver?.fullName || "",
            lat: Number(coords.latitude),
            lng: Number(coords.longitude),
            addressLabel: locationLabel,
          })
        );
      } catch {
        /* ignore */
      }
    }
    if (
      !requireCustomerAuth(navigate, {
        basePath: "/",
        returnTo: "/",
        caregiverId: caregiver?.id || caregiver?.caregiverId,
        action: AUTH_ACTIONS.HIRE,
        tab: "create",
        ...authSearchContext(),
      })
    ) {
      return;
    }
    // Open booking wizard (patient select) first; Care Pass is checked after a patient is chosen.
    setHireCaregiver(caregiver);
  }

  useResumeProtectedAction({
    getCaregiverById: (id) => filtered.find((c) => String(c.id) === String(id)),
    onResumeCall: performCall,
  });

  function bookFromPreference(caregiver) {
    handleHire(caregiver);
  }

  function handleViewProfile(caregiver) {
    recordViewedCaregiver(caregiver);
    navigate(`/caregivers/${caregiver.id}`, {
      state: {
        caregiver,
        searchLocation: locationLabel,
        lat: coords.latitude,
        lng: coords.longitude,
        searchDate,
        searchTime,
        searchHours,
        planType,
      },
    });
  }

  function handleLocationSelect(city) {
    if (!city) return;
    userOverrideLocationRef.current = true;
    setNearMeActive(false);
    setRadiusKm(marketplaceRadiusForLocation(city));
    setLocationLabel(city.label);
    setCoords({
      latitude: String(city.latitude),
      longitude: String(city.longitude),
    });
    persistMarketplaceLocation({
      label: city.label,
      latitude: city.latitude,
      longitude: city.longitude,
    });
  }

  function handleLocationChange(label) {
    setLocationLabel(label);
    if (nearMeActive) {
      setNearMeActive(false);
      setRadiusKm(DEFAULT_MARKETPLACE_RADIUS_KM);
    }
    // Do not resolve on every keystroke — coords stay until select/commit.
    const trimmed = String(label || "").trim();
    const committed = resolveIndianCity(trimmed);
    // Accept aliases (Bangalore → Bengaluru) without requiring exact catalog label text.
    if (!committed) {
      setCoords({ latitude: "", longitude: "" });
    } else {
      setCoords({
        latitude: String(committed.latitude),
        longitude: String(committed.longitude),
      });
    }
  }

  async function handleNearMe() {
    userOverrideLocationRef.current = true;
    setNearMeLoading(true);
    setSearchError("");
    try {
      const committedCity = resolveIndianCity(locationLabel || "");
      const result = await captureNearMeMarketplaceLocation({
        fallback:
          committedCity && coords?.latitude && coords?.longitude
            ? {
                latitude: coords.latitude,
                longitude: coords.longitude,
                locationLabel: committedCity.label || locationLabel,
              }
            : committedCity
              ? {
                  latitude: committedCity.latitude,
                  longitude: committedCity.longitude,
                  locationLabel: committedCity.label,
                }
              : null,
      });
      if (!result.ok) {
        showToast.error(result.error);
        return false;
      }
      setCoords(result.coords);
      setLocationLabel(result.locationLabel);
      setRadiusKm(result.radiusKm);
      setNearMeActive(true);
      setCareTypeEngaged(true);
      persistMarketplaceLocation({
        label: result.locationLabel,
        latitude: result.coords?.latitude,
        longitude: result.coords?.longitude,
      });
      if (result.usedFallback) {
        showToast.info(
          `Couldn't read GPS — searching near ${result.locationLabel} using your selected area.`
        );
      }
      return true;
    } catch (err) {
      showToast.error(err?.message || "Unable to use your location. Please try again.");
      return false;
    } finally {
      setNearMeLoading(false);
    }
  }

  function scrollToCaregivers() {
    if (!activeCareTypeBookingEnabled) {
      const availability = getCareTypeAvailability(serviceTab, marketplaceTypes);
      showToast.info(`${CARE_TYPES[serviceTab]?.label || "This service"} is ${availability.statusLabel}. Booking is not available yet.`);
      setSearchError(`${availability.statusLabel}: booking is not available for this service yet.`);
      return;
    }
    const validationError = getMarketplaceSearchValidationError({
      careTypeEngaged,
      serviceTab,
      locationLabel,
      coords,
      searchDate,
    });
    if (validationError) {
      setSearchError(validationError);
      return;
    }
    setSearchError("");
    document.getElementById("caregivers")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function handleWizardComplete({ duration, careTypes }) {
    const primaryCare = careTypes?.[0] || DEFAULT_MARKETPLACE_CARE_TYPE;
    setServiceTab(primaryCare);
    if (primaryCare === "physio") {
      setSearchShift("hourly");
      setSearchShiftChosen(true);
    } else if (duration) {
      setSearchShift(duration);
      setSearchShiftChosen(true);
    }
    setCareTypeEngaged(true);
    setFilters(createDefaultFilterState(primaryCare));
    scrollToCaregivers();
  }

  return (
    <div className="landing-page">
      <PublicHeader active="find" />

      <MarketplaceHero />

      <MarketplaceSearchBar
        serviceTab={serviceTab}
        onServiceTabChange={setServiceTab}
        careTypeTabs={careTypeTabs}
        careTypeEngaged={careTypeEngaged}
        onCareTypeEngaged={setCareTypeEngaged}
        locationLabel={locationLabel}
        onLocationChange={handleLocationChange}
        onLocationSelect={handleLocationSelect}
        searchDate={searchDate}
        onSearchDateChange={setSearchDate}
        searchTime={searchTime}
        onSearchTimeChange={setSearchTime}
        searchShift={searchShift}
        onSearchShiftChange={(next) => {
          setSearchShift(next);
          setSearchShiftChosen(true);
        }}
        searchShiftOptions={shiftOptionsForCareType}
        onSearch={scrollToCaregivers}
        promptLocation={promptLocation}
        promptDate={promptDate}
        locationComplete={locationComplete}
        canSearch={canSearch}
        nearMeActive={nearMeActive}
        nearMeLoading={nearMeLoading}
        onNearMe={handleNearMe}
      />
      {searchError ? <p className="landing-results__error landing-search__error">{searchError}</p> : null}

      {/* Results render when bound search filters are valid (canSearch) */}
      {!canSearch ? (
        <p className="landing-results__hint landing-results__hint--hero">
          Select a care type, location, and start date, then search to browse verified caregivers.
        </p>
      ) : null}

      {canSearch ? (
      <main
        id="caregivers"
        className={`landing-main landing-main--results${showShortlist ? "" : " landing-main--no-prefs"}`}
      >
        <MarketplaceFilterPanel
          careType={serviceTab || "attendant"}
          filters={filters}
          onChange={setFilters}
          onClear={clearFilters}
          planType={planType}
        />

        <section className="landing-results">
          <MarketplaceResultsToolbar
            count={filtered.length}
            locationLabel={locationLabel}
            sortBy={sortBy}
            onSortChange={setSortBy}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />

          {error ? <p className="landing-results__error">{error}</p> : null}
          {loading ? <p className="landing-results__loading">Loading caregivers…</p> : null}
          {!loading && filtered.length === 0 ? (
            <p className="landing-results__empty">No caregivers match your filters. Try widening the search radius.</p>
          ) : null}

          {viewMode === "map" && !loading && filtered.length > 0 ? (
            <MarketplaceCaregiverMap
              caregivers={filtered}
              customerLat={Number(coords.latitude)}
              customerLng={Number(coords.longitude)}
              locationLabel={locationLabel}
              selectedId={selectedMapCaregiverId}
              onSelectCaregiver={(caregiver) => setSelectedMapCaregiverId(String(caregiver.id))}
              onViewProfile={handleViewProfile}
              onHire={handleHire}
              getInitials={getInitials}
              formatRating={formatRating}
              formatMoney={formatMoney}
              planType={planType}
              isFav={(cg) => preferenceIds.has(String(cg.id))}
              onToggleFav={(cg) => {
                if (preferenceIds.has(String(cg.id))) removeFromPreference(cg.id);
                else addToPreference(cg);
              }}
            />
          ) : (
          <div className={`landing-grid landing-grid--${viewMode}`}>
            {filtered.map((c) => (
              <CaregiverListingCard
                key={c.id}
                caregiver={c}
                getInitials={getInitials}
                formatRating={formatRating}
                formatMoney={formatMoney}
                planType={planType}
                onViewProfile={handleViewProfile}
                onHire={handleHire}
                onCall={handleCall}
                isFav={preferenceIds.has(String(c.id))}
                onToggleFav={(cg) => {
                  if (preferenceIds.has(String(cg.id))) removeFromPreference(cg.id);
                  else addToPreference(cg);
                }}
              />
            ))}
          </div>
          )}
        </section>

        {showShortlist ? (
          <aside className="landing-prefs-column">
            <PreferredCaregiversPanel
              preferences={filteredPreferences}
              onRemove={removeFromPreference}
              onMove={movePreference}
              onBook={handleCall}
            />
          </aside>
        ) : null}
      </main>
      ) : null}

      <HowItWorksSection
        latitude={coords.latitude}
        longitude={coords.longitude}
        cityLabel={locationLabel}
      />

      <section className="savings-banner-section" aria-label="Savings with NEMA">
        <SavingsBanner onCalculate={() => navigate("/pricing")} />
      </section>

      <section className="nema-assurance-section" aria-label="The NEMA assurance">
        <NemaAssuranceSection onExplore={scrollToCaregivers} />
      </section>

      <section className="landing-pricing-hero" aria-label="Pricing and plans">
        <PricingHeroPlan
          onViewDetails={(planId) =>
            navigate(planId ? `/pricing?plan=${encodeURIComponent(planId)}#pricing-plans` : "/pricing#pricing-plans")
          }
        />
      </section>

      <section className="trust-highlights-section" aria-label="Care highlights">
        <TrustHighlightsBar />
      </section>

      <section className="npid-dash-section" aria-label="NEMA CareHub Identity">
        <NpidDashboardSection onApply={() => navigate("/caregiver/register")} />
      </section>

      <CareMatchingWizard
        onComplete={handleWizardComplete}
        enabledCareTypes={enabledCareTypes}
        enabledServiceTypes={enabledServiceTypes}
      />

      <section className="safety-trust-section" aria-label="Caregiver verification and safety">
        <SafetyTrustBanner />
      </section>

      <section id="faqs" className="landing-faq-section" aria-label="Frequently asked questions">
        <LandingFaqSection />
      </section>

      <section id="care-manager" className="cm-premium-section" aria-label="Care Manager premium support">
        <CareManagerPremiumSection onAddCareManager={() => navigate("/pricing")} />
      </section>

      <LandingSiteFooter />

      {showAuthOverlay ? <Login asOverlay onDismiss={closeAuthOverlay} /> : null}
      {hireCaregiver ? (
        <CareAttendantBookingDialog
          open
          caregiver={hireCaregiver}
          onClose={() => {
            setHireCaregiver(null);
            setBookingResumeDraft(null);
          }}
          resumeDraft={bookingResumeDraft}
          lat={coords.latitude ? Number(coords.latitude) : null}
          lng={coords.longitude ? Number(coords.longitude) : null}
          addressLabel={locationLabel}
          searchShift={searchShiftChosen ? searchShift : ""}
          shiftOptions={shiftOptionsForCareType}
        />
      ) : null}
      <FreemiumPaywall
        open={paywallOpen}
        onClose={() => {
          setResumeAfterPaywall(false);
          closePaywall();
        }}
        onSubscribed={refreshFreemium}
        resumeBooking={resumeAfterPaywall}
      />
      {gateModal}
      {inAppCallSheet}
    </div>
  );
}
